import { Icon } from "./Icons";
import { nextSteps, stageLabel, type NextStep } from "../lib/nextSteps";
import type { Project } from "../lib/types";

/* What's standing between the project and its next stage. The client sees only
   what's theirs to do; the studio sees both sides, so it's obvious who's holding
   things up. */

function Row({ s, showWho }: { s: NextStep; showWho: boolean }) {
  return (
    <div className="row between wrap" style={{ padding: "11px 0", gap: 10, alignItems: "flex-start" }}>
      <div className="row" style={{ gap: 10, alignItems: "flex-start", minWidth: 0 }}>
        <span style={{ width: 17, height: 17, borderRadius: 17, flexShrink: 0, marginTop: 1, display: "grid", placeItems: "center",
          border: "1.5px solid " + (s.tone === "warn" ? "var(--warn)" : "var(--accent)"), background: "transparent" }}>
          <span style={{ width: 5, height: 5, borderRadius: 5, background: s.tone === "warn" ? "var(--warn)" : "var(--accent)" }} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>{s.label}</div>
          {s.detail && <div className="dim" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.45 }}>{s.detail}</div>}
        </div>
      </div>
      <span className="row" style={{ gap: 6, flexShrink: 0 }}>
        {showWho && <span className="badge" style={{ fontSize: 9 }}>{s.who === "client" ? "Client" : "Studio"}</span>}
        <span className="badge" style={{ fontSize: 9, opacity: 0.75 }}>{stageLabel(s.stage)}</span>
      </span>
    </div>
  );
}

/** Client-facing: only what they owe. */
export function ClientActions({ p, go }: { p: Project; go?: (tab: string) => void }) {
  const mine = nextSteps(p).filter((s) => s.who === "client");
  if (!mine.length) {
    return (
      <div className="card pad reveal" style={{ gridColumn: "1 / -1", background: "color-mix(in srgb, var(--ok) 5%, var(--card))", borderColor: "color-mix(in srgb, var(--ok) 30%, var(--line))" }}>
        <div className="row" style={{ gap: 10 }}>
          <Icon.checkCircle width={18} style={{ color: "var(--ok)" }} />
          <span style={{ fontSize: 14 }}>Nothing needed from you right now — it's with us. We'll be in touch.</span>
        </div>
      </div>
    );
  }
  return (
    <div className="card pad reveal" style={{ gridColumn: "1 / -1" }}>
      <div className="row between wrap" style={{ gap: 10, marginBottom: 4 }}>
        <div>
          <div className="kicker">Needs you</div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>{mine.length} thing{mine.length > 1 ? "s" : ""} holding the project up — each one moves it to the next stage</div>
        </div>
      </div>
      <div className="col" style={{ gap: 0 }}>
        {mine.map((s, i) => (
          <div key={s.id} style={{ borderTop: i ? "1px solid var(--line)" : "none" }}>
            <Row s={s} showWho={false} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Studio-facing: both sides, so it's clear who's blocking. */
export function StudioActions({ p }: { p: Project }) {
  const all = nextSteps(p);
  if (!all.length) {
    return (
      <div className="card pad reveal">
        <div className="row" style={{ gap: 10 }}>
          <Icon.checkCircle width={18} style={{ color: "var(--ok)" }} />
          <span style={{ fontSize: 14 }}>Nothing outstanding — this project is clear to move.</span>
        </div>
      </div>
    );
  }
  const onUs = all.filter((s) => s.who === "studio").length;
  const onThem = all.length - onUs;
  return (
    <div className="card pad reveal">
      <div className="row between wrap" style={{ gap: 10, marginBottom: 4 }}>
        <div>
          <div className="kicker">To advance this project</div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>
            {onUs ? `${onUs} on us` : "nothing on us"}{onThem ? ` · ${onThem} with ${p.client.name.split(" ")[0]}` : ""}
          </div>
        </div>
      </div>
      <div className="col" style={{ gap: 0 }}>
        {all.map((s, i) => (
          <div key={s.id} style={{ borderTop: i ? "1px solid var(--line)" : "none" }}>
            <Row s={s} showWho />
          </div>
        ))}
      </div>
    </div>
  );
}
